// Property management phase 8 — the commercial sub-module (owner, 17 Oct 2026).
//
//   Leases          each commercial lease: terms (NNN / gross, area, caps, percentage
//                   rent, letter of credit), the base rent schedule, options and their
//                   notice dates, tenant insurance, sales reports and percentage rent
//   Expense pools   per building per year: budget -> monthly estimates; actuals ->
//                   year-end reconciliation, approved and billed, with a printable
//                   statement for the tenant
//
// Every figure comes from the server (routes/pmCommercial.js -> lib/pmCommercialCalc.js);
// this screen never computes a charge itself.
import React, { useCallback, useEffect, useState } from 'react';
import { useApiClient } from '../../hooks/useApiClient';
import {
    PageHeader, Panel, Tabs, Table, Pill, Input, Select, Textarea, Button, Notice, Loading, Empty, money, fmtDate, titleCase,
} from '../../components/secure/pm/PMKit';

const API = '/api/property-management/commercial';
const STRUCTURES = [
    { value: 'nnn', label: 'NNN (triple net)' }, { value: 'absolute_net', label: 'Absolute net' },
    { value: 'modified_gross', label: 'Modified gross' }, { value: 'full_service_gross', label: 'Full-service gross' },
    { value: 'percentage', label: 'Percentage (retail)' },
];
const STRUCT_LABEL = Object.fromEntries(STRUCTURES.map((s) => [s.value, s.label]));
const OPTION_TYPES = ['renewal', 'expansion', 'contraction', 'termination', 'rofr', 'rofo', 'purchase', 'relocation'];
const OPTION_LABEL = { rofr: 'Right of first refusal', rofo: 'Right of first offer' };
const CATS = [
    { value: 'cam', label: 'CAM (common area)' }, { value: 'utilities', label: 'Utilities' }, { value: 'management', label: 'Management' },
    { value: 'tax', label: 'Real estate tax' }, { value: 'insurance', label: 'Insurance' }, { value: 'other', label: 'Other' },
];
const errOf = (e) => ({ message: e.message, problems: e.problems || (e.payload && e.payload.problems) });
const parseJson = (v) => { if (!v) return []; if (typeof v !== 'string') return v; try { return JSON.parse(v); } catch { return []; } };

// The statement is an HTML page behind the API's bearer token, so fetch it and open the result.
async function openStatement(reconId) {
    let token = null;
    try { token = localStorage.getItem('jwtToken'); } catch { /* storage blocked */ }
    const r = await fetch(`${API}/reconciliations/${reconId}/statement`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    const html = await r.text();
    const w = window.open('', '_blank');
    if (w) { w.document.open(); w.document.write(html); w.document.close(); }
}

export default function PMCommercialPage() {
    const [tab, setTab] = useState('leases');
    return (
        <div>
            <PageHeader title="Commercial Leasing"
                subtitle="Commercial lease terms, rent schedules, options, tenant insurance, percentage rent and the yearly CAM / tax / insurance reconciliation." />
            <Tabs tabs={[{ key: 'leases', label: 'Leases' }, { key: 'pools', label: 'Expense pools (CAM)' }]} active={tab} onChange={setTab} />
            {tab === 'leases' ? <LeasesTab /> : <PoolsTab />}
        </div>
    );
}

// ======================================================================= leases
function LeasesTab() {
    const api = useApiClient();
    const [rows, setRows] = useState(null);
    const [open, setOpen] = useState(null);
    const [error, setError] = useState(null);
    const load = useCallback(() => api.get(`${API}/leases`).then(setRows).catch((e) => setError(errOf(e))), [api]);
    useEffect(() => { load(); }, [load]);
    if (error) return <Notice {...error} />;
    if (!rows) return <Loading />;
    if (open) return <LeaseDetail leaseId={open} onBack={() => { setOpen(null); load(); }} />;
    return (
        <Panel title="Commercial leases" subtitle="Create the lease on the Leasing page against a commercial unit, then set its commercial terms here.">
            {rows.length ? (
                <Table csvName="commercial-leases" onRowClick={(r) => setOpen(r.lease_id)} rows={rows} columns={[
                    { key: 'property_name', label: 'Property' },
                    { key: 'unit_number', label: 'Unit' },
                    { key: 'tenants', label: 'Tenant' },
                    { key: 'structure', label: 'Structure', render: (r) => (r.structure ? STRUCT_LABEL[r.structure] : <Pill tone="amber">Terms not set</Pill>) },
                    { key: 'rentable_sf', label: 'RSF', align: 'right', render: (r) => (r.rentable_sf ? Number(r.rentable_sf).toLocaleString() : '—') },
                    { key: 'rent_amount', label: 'Monthly rent', align: 'right', render: (r) => money(r.rent_amount) },
                    { key: 'end_date', label: 'Expires', nowrap: true, render: (r) => fmtDate(r.end_date) },
                    { key: 'next_option_deadline', label: 'Next option notice', nowrap: true, render: (r) => fmtDate(r.next_option_deadline) },
                    { key: 'status', label: 'Status', render: (r) => <Pill>{titleCase(r.status)}</Pill> },
                ]} />
            ) : <Empty title="No commercial leases yet" message="A commercial lease is created on the Leasing page against a unit whose use is commercial." />}
        </Panel>
    );
}

function LeaseDetail({ leaseId, onBack }) {
    const api = useApiClient();
    const [d, setD] = useState(null);
    const [msg, setMsg] = useState(null);
    const load = useCallback(() => api.get(`${API}/leases/${leaseId}`).then(setD).catch((e) => setMsg({ tone: 'bad', ...errOf(e) })), [api, leaseId]);
    useEffect(() => { load(); }, [load]);
    if (!d) return msg ? <Notice {...msg} /> : <Loading />;
    const L = d.lease;
    const done = (m) => { setMsg({ tone: 'good', message: m }); load(); };
    const failed = (e) => setMsg({ tone: 'bad', ...errOf(e) });
    return (
        <div>
            <button onClick={onBack} className="text-sm text-indigo-700 mb-3">← All commercial leases</button>
            <h2 className="text-lg font-semibold text-slate-800">{L.property_name} · Unit {L.unit_number}</h2>
            <p className="text-sm text-slate-500 mb-4">{L.tenants || 'No tenant linked'} · {fmtDate(L.start_date)} to {fmtDate(L.end_date)} · {titleCase(L.status)}</p>
            {msg && <Notice {...msg} onDismiss={() => setMsg(null)} />}
            <TermsPanel d={d} api={api} onDone={done} onFail={failed} />
            <RentSchedulePanel d={d} api={api} onDone={done} onFail={failed} />
            <OptionsPanel d={d} api={api} onDone={done} onFail={failed} />
            <InsurancePanel d={d} api={api} onDone={done} onFail={failed} />
            {d.terms && d.terms.breakpoint_type && d.terms.breakpoint_type !== 'none' && <SalesPanel d={d} api={api} onDone={done} onFail={failed} />}
            {d.reconciliations.length > 0 && (
                <Panel title="Reconciliations">
                    <Table rows={d.reconciliations} toolbar={false} columns={[
                        { key: 'year', label: 'Year' },
                        { key: 'tenant_cost', label: 'Tenant cost', align: 'right', render: (r) => money(r.tenant_cost) },
                        { key: 'estimates_billed', label: 'Estimates billed', align: 'right', render: (r) => money(r.estimates_billed) },
                        { key: 'balance', label: 'Balance', align: 'right', render: (r) => money(r.balance) },
                        { key: 'status', label: 'Status', render: (r) => <Pill>{titleCase(r.status)}</Pill> },
                        { key: 'x', label: '', sortable: false, render: (r) => <button className="text-indigo-700 text-sm" onClick={() => openStatement(r.recon_id)}>Statement</button> },
                    ]} />
                </Panel>
            )}
        </div>
    );
}

function TermsPanel({ d, api, onDone, onFail }) {
    const blank = { structure: 'nnn', cam_cap_type: 'none', breakpoint_type: 'none', sales_report_frequency: 'none', deposit_form: 'cash', audit_window_days: 90 };
    const [f, setF] = useState({ ...blank, ...(d.terms || {}) });
    const [busy, setBusy] = useState(false);
    const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
    const gross = ['modified_gross', 'full_service_gross'].includes(f.structure);
    const save = async () => {
        setBusy(true);
        try { await api.put(`${API}/leases/${d.lease.lease_id}/terms`, f); onDone('Commercial terms saved.'); }
        catch (e) { onFail(e); } finally { setBusy(false); }
    };
    return (
        <Panel title="Commercial terms" subtitle={d.terms ? null : 'Not set yet. Rentable area and structure are needed before CAM can be shared.'}>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4">
                <Select label="Lease structure" value={f.structure} onChange={set('structure')} options={STRUCTURES} />
                <Input label="Rentable sq ft" value={f.rentable_sf ?? ''} onChange={set('rentable_sf')} inputMode="numeric" />
                <Input label="Usable sq ft" value={f.usable_sf ?? ''} onChange={set('usable_sf')} inputMode="numeric" />
                <Input label="Base rent $/sq ft/year" value={f.base_rent_psf_annual ?? ''} onChange={set('base_rent_psf_annual')} inputMode="decimal" />
                {gross && <>
                    <Input label="Base year" value={f.base_year ?? ''} onChange={set('base_year')} inputMode="numeric" hint="Tenant pays increases over this year" />
                    <Input label="or expense stop $/sq ft" value={f.expense_stop_psf ?? ''} onChange={set('expense_stop_psf')} inputMode="decimal" />
                </>}
                {!gross && <>
                    <Select label="CAM cap" value={f.cam_cap_type} onChange={set('cam_cap_type')}
                        options={[{ value: 'none', label: 'No cap' }, { value: 'non_cumulative', label: 'Non-cumulative' }, { value: 'cumulative', label: 'Cumulative' }]} />
                    {f.cam_cap_type !== 'none' && <Input label="Cap % a year" value={f.cam_cap_pct ?? ''} onChange={set('cam_cap_pct')} inputMode="decimal" hint="On controllable CAM only" />}
                    {f.cam_cap_type === 'cumulative' && <>
                        <Input label="Cap base amount $" value={f.cap_base_amount ?? ''} onChange={set('cap_base_amount')} inputMode="decimal" />
                        <Input label="Cap base year" value={f.cap_base_year ?? ''} onChange={set('cap_base_year')} inputMode="numeric" />
                    </>}
                </>}
                <Input label="Admin fee % (on CAM)" value={f.admin_fee_pct ?? ''} onChange={set('admin_fee_pct')} inputMode="decimal" />
                <Input label="Gross-up to occupancy %" value={f.gross_up_pct ?? ''} onChange={set('gross_up_pct')} inputMode="decimal" hint="e.g. 95" />
                <Select label="Percentage rent" value={f.breakpoint_type} onChange={set('breakpoint_type')}
                    options={[{ value: 'none', label: 'None' }, { value: 'natural', label: 'Natural breakpoint' }, { value: 'artificial', label: 'Artificial breakpoint' }]} />
                {f.breakpoint_type !== 'none' && <>
                    <Input label="Rate % of sales" value={f.pct_rent_rate ?? ''} onChange={set('pct_rent_rate')} inputMode="decimal" />
                    {f.breakpoint_type === 'artificial' && <Input label="Breakpoint $ (annual)" value={f.breakpoint_amount ?? ''} onChange={set('breakpoint_amount')} inputMode="decimal" />}
                    <Select label="Sales reports" value={f.sales_report_frequency} onChange={set('sales_report_frequency')} options={['none', 'monthly', 'quarterly', 'annual']} />
                </>}
                <Input label="TI allowance $" value={f.ti_allowance ?? ''} onChange={set('ti_allowance')} inputMode="decimal" />
                <Input label="Free rent (months)" value={f.free_rent_months ?? ''} onChange={set('free_rent_months')} inputMode="numeric" />
                <Select label="Security" value={f.deposit_form} onChange={set('deposit_form')}
                    options={[{ value: 'cash', label: 'Cash deposit' }, { value: 'letter_of_credit', label: 'Letter of credit' }, { value: 'none', label: 'None' }]} />
                {f.deposit_form === 'letter_of_credit' && <>
                    <Input label="LOC amount $" value={f.loc_amount ?? ''} onChange={set('loc_amount')} inputMode="decimal" />
                    <Input label="LOC bank" value={f.loc_bank ?? ''} onChange={set('loc_bank')} />
                    <Input label="LOC expires" type="date" value={f.loc_expires_on ?? ''} onChange={set('loc_expires_on')} />
                </>}
                <Input label="Tenant audit window (days)" value={f.audit_window_days ?? 90} onChange={set('audit_window_days')} inputMode="numeric" />
                <Input label="Guarantor" value={f.guarantor ?? ''} onChange={set('guarantor')} />
                <Input label="Permitted use" value={f.permitted_use ?? ''} onChange={set('permitted_use')} className="md:col-span-2" />
                <Input label="Exclusive use" value={f.exclusive_use ?? ''} onChange={set('exclusive_use')} className="md:col-span-2" />
            </div>
            <Textarea label="Notes" value={f.notes ?? ''} onChange={set('notes')} />
            {d.can_write && <Button onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save terms'}</Button>}
        </Panel>
    );
}

function RentSchedulePanel({ d, api, onDone, onFail }) {
    const [f, setF] = useState({ first_monthly: d.lease.rent_amount || '', escalation_type: 'fixed', rate: '3', step_amount: '', free_rent_months: d.terms ? d.terms.free_rent_months || '' : '' });
    const [preview, setPreview] = useState(null);
    const [busy, setBusy] = useState(false);
    const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
    const build = async () => {
        setBusy(true);
        try { setPreview(await api.post(`${API}/leases/${d.lease.lease_id}/rent-schedule/preview`, f)); } catch (e) { onFail(e); } finally { setBusy(false); }
    };
    const apply = async () => {
        setBusy(true);
        try {
            const r = await api.post(`${API}/leases/${d.lease.lease_id}/rent-schedule/apply`, { ...f, steps: preview.steps });
            setPreview(null);
            onDone(`Rent schedule saved: ${r.charges_created} billing rule(s).${r.already_billed_through ? ` Periods already billed through ${fmtDate(r.already_billed_through)} were left as billed.` : ''}`);
        } catch (e) { onFail(e); } finally { setBusy(false); }
    };
    const stepCols = [
        { key: 'starts_on', label: 'From', nowrap: true, render: (r) => fmtDate(r.starts_on) },
        { key: 'ends_on', label: 'To', nowrap: true, render: (r) => fmtDate(r.ends_on) },
        { key: 'monthly_amount', label: 'Monthly', align: 'right', render: (r) => money(r.monthly_amount) },
        { key: 'annual_psf', label: '$/sq ft/yr', align: 'right', render: (r) => (r.annual_psf ? Number(r.annual_psf).toFixed(2) : '—') },
        { key: 'basis', label: 'Basis' },
    ];
    return (
        <Panel title="Base rent schedule" subtitle="Escalations are billed from the first full month on or after each lease anniversary. Free rent covers the first rent periods.">
            {d.steps.length > 0 && !preview && <Table rows={d.steps} toolbar={false} columns={stepCols} />}
            {d.can_write && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-x-4 mt-3 items-end">
                    <Input label="Year 1 monthly rent $" value={f.first_monthly} onChange={set('first_monthly')} inputMode="decimal" />
                    <Select label="Escalation" value={f.escalation_type} onChange={set('escalation_type')}
                        options={[{ value: 'fixed', label: 'Fixed % a year' }, { value: 'step', label: 'Fixed $ a year' }, { value: 'cpi', label: 'CPI (enter yearly)' }, { value: 'none', label: 'None (flat)' }]} />
                    {f.escalation_type === 'fixed' && <Input label="% a year" value={f.rate} onChange={set('rate')} inputMode="decimal" />}
                    {f.escalation_type === 'step' && <Input label="$ a month added yearly" value={f.step_amount} onChange={set('step_amount')} inputMode="decimal" />}
                    <Input label="Free rent months" value={f.free_rent_months} onChange={set('free_rent_months')} inputMode="numeric" />
                    <div className="mb-3"><Button variant="ghost" onClick={build} disabled={busy}>Build schedule</Button></div>
                </div>
            )}
            {preview && (
                <div className="mt-2">
                    {(preview.notes || []).map((n) => <Notice key={n} tone="warn" message={n} />)}
                    <Table rows={preview.steps} toolbar={false} columns={stepCols} />
                    <div className="flex gap-2 mt-3">
                        <Button onClick={apply} disabled={busy}>Save this schedule and bill from it</Button>
                        <Button variant="ghost" onClick={() => setPreview(null)}>Cancel</Button>
                    </div>
                </div>
            )}
        </Panel>
    );
}

function OptionsPanel({ d, api, onDone, onFail }) {
    const [f, setF] = useState({ option_type: 'renewal', description: '', window_opens_on: '', notice_deadline: '' });
    const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
    const add = async () => {
        try { await api.post(`${API}/leases/${d.lease.lease_id}/options`, f); setF({ ...f, description: '', window_opens_on: '', notice_deadline: '' }); onDone('Option added. Its notice deadline is on the Compliance Calendar.'); }
        catch (e) { onFail(e); }
    };
    const setStatus = async (o, status) => {
        try { await api.put(`${API}/options/${o.option_id}`, { status }); onDone(`Option marked ${status}.`); } catch (e) { onFail(e); }
    };
    return (
        <Panel title="Options and critical dates" subtitle="Each notice deadline goes on the Compliance Calendar.">
            {d.options.length > 0 && <Table rows={d.options} toolbar={false} columns={[
                { key: 'option_type', label: 'Option', render: (r) => OPTION_LABEL[r.option_type] || titleCase(r.option_type) },
                { key: 'description', label: 'Terms' },
                { key: 'window_opens_on', label: 'Window opens', nowrap: true, render: (r) => fmtDate(r.window_opens_on) },
                { key: 'notice_deadline', label: 'Notice by', nowrap: true, render: (r) => fmtDate(r.notice_deadline) },
                { key: 'status', label: 'Status', render: (r) => <Pill>{titleCase(r.status)}</Pill> },
                { key: 'x', label: '', sortable: false, render: (r) => (d.can_write && r.status === 'open' ? (
                    <span className="whitespace-nowrap text-sm">
                        <button className="text-indigo-700 mr-2" onClick={() => setStatus(r, 'exercised')}>Exercised</button>
                        <button className="text-slate-500 mr-2" onClick={() => setStatus(r, 'waived')}>Waived</button>
                        <button className="text-slate-500" onClick={() => setStatus(r, 'lapsed')}>Lapsed</button>
                    </span>) : null) },
            ]} />}
            {d.can_write && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-x-4 mt-3 items-end">
                    <Select label="Option" value={f.option_type} onChange={set('option_type')} options={OPTION_TYPES.map((t) => ({ value: t, label: OPTION_LABEL[t] || titleCase(t) }))} />
                    <Input label="Terms" value={f.description} onChange={set('description')} placeholder="One 5-year term at 95% of market" />
                    <Input label="Window opens" type="date" value={f.window_opens_on} onChange={set('window_opens_on')} />
                    <Input label="Notice deadline" type="date" value={f.notice_deadline} onChange={set('notice_deadline')} />
                    <div className="mb-3"><Button variant="ghost" onClick={add}>Add option</Button></div>
                </div>
            )}
        </Panel>
    );
}

function InsurancePanel({ d, api, onDone, onFail }) {
    const [f, setF] = useState({ carrier: '', policy_no: '', coverage: 'General liability', per_occurrence: '', aggregate_limit: '', expires_on: '', additional_insured: true });
    const set = (k) => (e) => setF({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });
    const add = async () => {
        try {
            const r = await api.post(`${API}/leases/${d.lease.lease_id}/insurance`, f);
            setF({ ...f, carrier: '', policy_no: '', expires_on: '' });
            onDone(r.warning ? `Certificate saved. ${r.warning}` : 'Certificate saved. Its expiry is on the Compliance Calendar.');
        } catch (e) { onFail(e); }
    };
    const remove = async (c) => { try { await api.delete(`${API}/insurance/${c.coi_id}`); onDone('Certificate removed.'); } catch (e) { onFail(e); } };
    const today = new Date().toISOString().slice(0, 10);
    return (
        <Panel title="Tenant insurance certificates">
            {d.insurance.length > 0 && <Table rows={d.insurance} toolbar={false} columns={[
                { key: 'coverage', label: 'Coverage' }, { key: 'carrier', label: 'Carrier' }, { key: 'policy_no', label: 'Policy' },
                { key: 'per_occurrence', label: 'Per occurrence', align: 'right', render: (r) => money(r.per_occurrence) },
                { key: 'additional_insured', label: 'Addl insured', render: (r) => (r.additional_insured ? 'Yes' : <Pill tone="amber">No</Pill>) },
                { key: 'expires_on', label: 'Expires', nowrap: true, render: (r) => (r.expires_on < today ? <Pill tone="rose">Expired {fmtDate(r.expires_on)}</Pill> : fmtDate(r.expires_on)) },
                { key: 'x', label: '', sortable: false, render: (r) => (d.can_write ? <button className="text-slate-500 text-sm" onClick={() => remove(r)}>Remove</button> : null) },
            ]} />}
            {d.can_write && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 mt-3 items-end">
                    <Input label="Carrier" value={f.carrier} onChange={set('carrier')} />
                    <Input label="Policy no." value={f.policy_no} onChange={set('policy_no')} />
                    <Select label="Coverage" value={f.coverage} onChange={set('coverage')} options={['General liability', 'Property', 'Umbrella', 'Workers compensation', 'Liquor liability', 'Other']} />
                    <Input label="Per occurrence $" value={f.per_occurrence} onChange={set('per_occurrence')} inputMode="decimal" />
                    <Input label="Aggregate $" value={f.aggregate_limit} onChange={set('aggregate_limit')} inputMode="decimal" />
                    <Input label="Expires" type="date" value={f.expires_on} onChange={set('expires_on')} />
                    <label className="flex items-center gap-2 text-sm mb-3"><input type="checkbox" checked={f.additional_insured} onChange={set('additional_insured')} />Owner and brokerage named as additional insured</label>
                    <div className="mb-3"><Button variant="ghost" onClick={add}>Add certificate</Button></div>
                </div>
            )}
        </Panel>
    );
}

function SalesPanel({ d, api, onDone, onFail }) {
    const [f, setF] = useState({ period_start: '', period_end: '', gross_sales: '', certified: false });
    const [year, setYear] = useState(new Date().getFullYear() - 1);
    const [calc, setCalc] = useState(null);
    const set = (k) => (e) => setF({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });
    const add = async () => { try { await api.post(`${API}/leases/${d.lease.lease_id}/sales`, f); setF({ period_start: '', period_end: '', gross_sales: '', certified: false }); onDone('Sales report saved.'); } catch (e) { onFail(e); } };
    const compute = async (bill) => {
        try {
            const r = await api.post(`${API}/leases/${d.lease.lease_id}/percentage-rent`, { year, bill });
            setCalc(r);
            if (bill) onDone(`Percentage rent of ${money(r.percentage_rent)} billed for ${year}.`);
        } catch (e) { onFail(e); }
    };
    return (
        <Panel title="Sales reports and percentage rent">
            {d.sales.length > 0 && <Table rows={d.sales} toolbar={false} columns={[
                { key: 'period_start', label: 'From', render: (r) => fmtDate(r.period_start) }, { key: 'period_end', label: 'To', render: (r) => fmtDate(r.period_end) },
                { key: 'gross_sales', label: 'Gross sales', align: 'right', render: (r) => money(r.gross_sales) },
                { key: 'certified', label: 'Certified', render: (r) => (r.certified ? 'Yes' : 'No') },
            ]} />}
            {d.can_write && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-x-4 mt-3 items-end">
                    <Input label="Period from" type="date" value={f.period_start} onChange={set('period_start')} />
                    <Input label="Period to" type="date" value={f.period_end} onChange={set('period_end')} />
                    <Input label="Gross sales $" value={f.gross_sales} onChange={set('gross_sales')} inputMode="decimal" />
                    <label className="flex items-center gap-2 text-sm mb-3"><input type="checkbox" checked={f.certified} onChange={set('certified')} />Certified by tenant</label>
                    <div className="mb-3"><Button variant="ghost" onClick={add}>Add report</Button></div>
                </div>
            )}
            <div className="flex flex-wrap gap-2 items-end mt-2">
                <Input label="Year" value={year} onChange={(e) => setYear(e.target.value)} inputMode="numeric" className="w-28" />
                <div className="mb-3"><Button variant="ghost" onClick={() => compute(false)}>Calculate</Button></div>
                {calc && calc.percentage_rent > 0 && d.can_write && <div className="mb-3"><Button onClick={() => compute(true)}>Bill {money(calc.percentage_rent)}</Button></div>}
            </div>
            {calc && (
                <p className="text-sm text-slate-600">
                    {calc.year}: sales {money(calc.sales)} from {calc.reports} report(s); breakpoint {money(calc.breakpoint)}
                    {' '}({calc.months_occupied} months); over the breakpoint {money(calc.over_breakpoint)} at {calc.rate_pct}% = <b>{money(calc.percentage_rent)}</b>.
                </p>
            )}
        </Panel>
    );
}

// ======================================================================= pools
function PoolsTab() {
    const api = useApiClient();
    const [data, setData] = useState(null);
    const [open, setOpen] = useState(null);
    const [f, setF] = useState({ pm_property_id: '', year: new Date().getFullYear(), total_rsf: '', occupancy_pct: '', copy_previous: true });
    const [msg, setMsg] = useState(null);
    const load = useCallback(() => api.get(`${API}/pools`).then((r) => {
        setData(r);
        setF((x) => (x.pm_property_id || !r.properties.length ? x : { ...x, pm_property_id: r.properties[0].pm_property_id }));
    }).catch((e) => setMsg({ tone: 'bad', ...errOf(e) })), [api]);
    useEffect(() => { load(); }, [load]);
    if (open) return <PoolDetail poolId={open} onBack={() => { setOpen(null); load(); }} />;
    if (!data) return msg ? <Notice {...msg} /> : <Loading />;
    const set = (k) => (e) => setF({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });
    const create = async () => {
        try { const r = await api.post(`${API}/pools`, f); setOpen(r.pool_id); } catch (e) { setMsg({ tone: 'bad', ...errOf(e) }); }
    };
    return (
        <div>
            {msg && <Notice {...msg} onDismiss={() => setMsg(null)} />}
            <Panel title="Operating expense pools" subtitle="One per commercial building per year: the budget drives monthly estimates; the actuals drive the year-end reconciliation.">
                {data.pools.length ? <Table rows={data.pools} onRowClick={(r) => setOpen(r.pool_id)} csvName="expense-pools" columns={[
                    { key: 'property_name', label: 'Property' }, { key: 'year', label: 'Year' },
                    { key: 'total_rsf', label: 'Building RSF', align: 'right', render: (r) => Number(r.total_rsf).toLocaleString() },
                    { key: 'budget_total', label: 'Budget', align: 'right', render: (r) => money(r.budget_total) },
                    { key: 'actual_total', label: 'Actual', align: 'right', render: (r) => (Number(r.missing_actuals) ? <span className="text-slate-400">{Number(r.missing_actuals)} missing</span> : money(r.actual_total)) },
                    { key: 'status', label: 'Status', render: (r) => <Pill>{titleCase(r.status)}</Pill> },
                ]} /> : <Empty title="No expense pools yet" message="Create one for each commercial building and year." />}
            </Panel>
            <Panel title="New expense pool">
                {data.properties.length ? (
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-x-4 items-end">
                        <Select label="Property" value={f.pm_property_id} onChange={set('pm_property_id')} options={data.properties.map((p) => ({ value: p.pm_property_id, label: p.name }))} />
                        <Input label="Year" value={f.year} onChange={set('year')} inputMode="numeric" />
                        <Input label="Building rentable sq ft" value={f.total_rsf} onChange={set('total_rsf')} inputMode="numeric" />
                        <label className="flex items-center gap-2 text-sm mb-3"><input type="checkbox" checked={f.copy_previous} onChange={set('copy_previous')} />Copy last year's expense lines</label>
                        <div className="mb-3"><Button onClick={create}>Create</Button></div>
                    </div>
                ) : <p className="text-sm text-slate-500">No commercial or mixed-use property is in your portfolio yet.</p>}
            </Panel>
        </div>
    );
}

function PoolDetail({ poolId, onBack }) {
    const api = useApiClient();
    const [d, setD] = useState(null);
    const [msg, setMsg] = useState(null);
    const [result, setResult] = useState(null);
    const [nx, setNx] = useState({ category: 'cam', label: '', budget: '', actual: '' });
    const [edits, setEdits] = useState({});
    const load = useCallback(() => api.get(`${API}/pools/${poolId}`).then((r) => { setD(r); setEdits({}); }).catch((e) => setMsg({ tone: 'bad', ...errOf(e) })), [api, poolId]);
    useEffect(() => { load(); }, [load]);
    if (!d) return msg ? <Notice {...msg} /> : <Loading />;
    const P = d.pool;
    const closed = P.status === 'closed';
    const run = async (fn, okMsg) => { try { const r = await fn(); if (okMsg) setMsg({ tone: 'good', message: typeof okMsg === 'function' ? okMsg(r) : okMsg }); await load(); return r; } catch (e) { setMsg({ tone: 'bad', ...errOf(e) }); return null; } };
    const addExpense = () => run(() => api.post(`${API}/pools/${poolId}/expenses`, nx), 'Expense added.').then(() => setNx({ category: nx.category, label: '', budget: '', actual: '' }));
    const edit = (x, k, v) => setEdits({ ...edits, [x.expense_id]: { ...(edits[x.expense_id] || {}), [k]: v } });
    const val = (x, k) => (edits[x.expense_id] && edits[x.expense_id][k] !== undefined ? edits[x.expense_id][k] : x[k] ?? '');
    const saveEdits = () => run(async () => { for (const [id, ch] of Object.entries(edits)) await api.put(`${API}/expenses/${id}`, ch); }, 'Expenses saved.');
    const estimates = async () => { const r = await run(() => api.post(`${API}/pools/${poolId}/estimates`, {}), 'Monthly estimates set for the year.'); if (r) setResult({ kind: 'estimates', ...r }); };
    const reconcile = async () => { const r = await run(() => api.post(`${API}/pools/${poolId}/reconcile`, {}), 'Draft reconciliations prepared. Review each, then approve.'); if (r) setResult({ kind: 'recon', ...r }); };
    const tot = (k) => d.expenses.reduce((s, x) => s + Number(x[k] || 0), 0);
    return (
        <div>
            <button onClick={onBack} className="text-sm text-indigo-700 mb-3">← All expense pools</button>
            <h2 className="text-lg font-semibold text-slate-800">{P.property_name} — {P.year}</h2>
            <p className="text-sm text-slate-500 mb-4">Building {Number(P.total_rsf).toLocaleString()} rentable sq ft · {titleCase(P.status)}</p>
            {msg && <Notice {...msg} onDismiss={() => setMsg(null)} />}

            <Panel title="Building">
                <PoolHeader pool={P} disabled={closed} onSave={(b) => run(() => api.put(`${API}/pools/${poolId}`, b), 'Saved.')} />
            </Panel>

            <Panel title="Recoverable expenses" subtitle="Controllable expenses are the ones a CAM cap limits; grossable ones are grossed up to the lease's occupancy when the building is partly empty."
                actions={Object.keys(edits).length ? <Button onClick={saveEdits}>Save changes</Button> : null}>
                {d.expenses.length > 0 && (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead><tr className="text-left text-xs uppercase text-slate-400"><th className="py-2">Category</th><th>Description</th><th className="text-right">Budget</th><th className="text-right">Actual</th><th className="text-center">Controllable</th><th className="text-center">Grossable</th><th /></tr></thead>
                            <tbody>
                                {d.expenses.map((x) => (
                                    <tr key={x.expense_id} className="border-t border-slate-100">
                                        <td className="py-1.5">{titleCase(x.category)}</td>
                                        <td><input className="p-1 border rounded w-full" disabled={closed} value={val(x, 'label')} onChange={(e) => edit(x, 'label', e.target.value)} /></td>
                                        <td className="text-right"><input className="p-1 border rounded w-28 text-right" disabled={closed} value={val(x, 'budget')} onChange={(e) => edit(x, 'budget', e.target.value)} /></td>
                                        <td className="text-right"><input className="p-1 border rounded w-28 text-right" disabled={closed} value={val(x, 'actual')} placeholder="—" onChange={(e) => edit(x, 'actual', e.target.value)} /></td>
                                        <td className="text-center"><input type="checkbox" disabled={closed} checked={!!Number(val(x, 'controllable'))} onChange={(e) => edit(x, 'controllable', e.target.checked ? 1 : 0)} /></td>
                                        <td className="text-center"><input type="checkbox" disabled={closed} checked={!!Number(val(x, 'grossable'))} onChange={(e) => edit(x, 'grossable', e.target.checked ? 1 : 0)} /></td>
                                        <td>{!closed && <button className="text-slate-400 text-xs" onClick={() => run(() => api.delete(`${API}/expenses/${x.expense_id}`), 'Removed.')}>Remove</button>}</td>
                                    </tr>
                                ))}
                                <tr className="border-t-2 border-slate-300 font-semibold"><td colSpan={2} className="py-2">Total</td><td className="text-right">{money(tot('budget'))}</td><td className="text-right">{money(tot('actual'))}</td><td colSpan={3} /></tr>
                            </tbody>
                        </table>
                    </div>
                )}
                {!closed && (
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-x-4 mt-3 items-end">
                        <Select label="Category" value={nx.category} onChange={(e) => setNx({ ...nx, category: e.target.value })} options={CATS} />
                        <Input label="Description" value={nx.label} onChange={(e) => setNx({ ...nx, label: e.target.value })} placeholder="Snow removal" />
                        <Input label="Budget $" value={nx.budget} onChange={(e) => setNx({ ...nx, budget: e.target.value })} inputMode="decimal" />
                        <Input label="Actual $ (year end)" value={nx.actual} onChange={(e) => setNx({ ...nx, actual: e.target.value })} inputMode="decimal" />
                        <div className="mb-3"><Button variant="ghost" onClick={addExpense}>Add expense</Button></div>
                    </div>
                )}
            </Panel>

            <Panel title="Tenants sharing this pool">
                {d.leases.length ? <Table rows={d.leases} toolbar={false} columns={[
                    { key: 'unit_number', label: 'Unit' }, { key: 'tenants', label: 'Tenant' },
                    { key: 'structure', label: 'Structure', render: (r) => (r.structure ? STRUCT_LABEL[r.structure] : <Pill tone="amber">Terms not set</Pill>) },
                    { key: 'rentable_sf', label: 'RSF', align: 'right', render: (r) => (r.rentable_sf ? Number(r.rentable_sf).toLocaleString() : '—') },
                    { key: 'share', label: 'Share', align: 'right', render: (r) => (r.rentable_sf ? `${((r.rentable_sf / P.total_rsf) * 100).toFixed(2)}%` : '—') },
                ]} /> : <Empty title="No commercial leases in this building for this year" />}
                {!closed && (
                    <div className="flex flex-wrap gap-2 mt-4">
                        <Button variant="ghost" onClick={estimates}>Set monthly estimates from the budget</Button>
                        <Button onClick={reconcile}>Reconcile the year from actuals</Button>
                    </div>
                )}
                {result && (
                    <div className="mt-3 text-sm space-y-1">
                        {result.leases.map((l) => (
                            <div key={l.lease_id} className="text-slate-600">
                                Unit {l.unit}: {l.skipped || l.kept || (result.kind === 'estimates'
                                    ? `${money(l.monthly_total)} a month from ${fmtDate(l.starts_on)}`
                                    : `cost ${money(l.tenant_cost)}, estimates ${money(l.estimates_billed)}, balance ${money(l.balance)}`)}
                                {(l.warnings || []).map((w) => <div key={w} className="text-amber-700 text-xs ml-4">{w}</div>)}
                            </div>
                        ))}
                    </div>
                )}
            </Panel>

            {d.reconciliations.length > 0 && (
                <Panel title={`Reconciliations ${P.year}`}>
                    <Table rows={d.reconciliations} toolbar={false} columns={[
                        { key: 'unit_number', label: 'Unit' },
                        { key: 'share_pct', label: 'Share', align: 'right', render: (r) => `${Number(r.share_pct).toFixed(3)}%` },
                        { key: 'months_occupied', label: 'Months', align: 'right' },
                        { key: 'tenant_cost', label: 'Cost', align: 'right', render: (r) => money(r.tenant_cost) },
                        { key: 'estimates_billed', label: 'Estimates', align: 'right', render: (r) => money(r.estimates_billed) },
                        { key: 'balance', label: 'Balance', align: 'right', render: (r) => <span className={Number(r.balance) < 0 ? 'text-emerald-700' : ''}>{money(r.balance)}</span> },
                        { key: 'status', label: 'Status', render: (r) => (
                            <div><Pill>{titleCase(r.status)}</Pill>{parseJson(r.warnings).length > 0 && <div className="text-xs text-amber-700 mt-1">{parseJson(r.warnings).join(' ')}</div>}</div>) },
                        { key: 'x', label: '', sortable: false, render: (r) => (
                            <span className="whitespace-nowrap text-sm">
                                <button className="text-indigo-700 mr-3" onClick={() => openStatement(r.recon_id)}>Statement</button>
                                {r.status === 'draft' && <button className="text-indigo-700 mr-3" onClick={() => run(() => api.post(`${API}/reconciliations/${r.recon_id}/approve`, {}),
                                    (x) => (x.status === 'billed' ? `Approved and billed ${money(x.balance)}, due in 30 days.` : x.status === 'credit_due' ? `Approved. A credit of ${money(-x.balance)} is due to the tenant: refund it by owner disbursement, then mark it credited.` : 'Approved; nothing owed either way.'))}>Approve</button>}
                                {r.status !== 'draft' && !r.sent_on && <button className="text-indigo-700 mr-3" onClick={() => run(() => api.post(`${API}/reconciliations/${r.recon_id}/sent`, {}), (x) => `Marked sent. The tenant's audit window closes ${fmtDate(x.audit_window_closes)} (on the Compliance Calendar).`)}>Mark sent</button>}
                                {r.status === 'credit_due' && <button className="text-indigo-700" onClick={() => run(() => api.post(`${API}/reconciliations/${r.recon_id}/credited`, {}), 'Marked credited.')}>Credited</button>}
                            </span>) },
                    ]} />
                </Panel>
            )}
        </div>
    );
}

function PoolHeader({ pool, disabled, onSave }) {
    const [f, setF] = useState({ total_rsf: pool.total_rsf, occupancy_pct: pool.occupancy_pct ?? '', notes: pool.notes ?? '' });
    const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
    return (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 items-end">
            <Input label="Building rentable sq ft" value={f.total_rsf} onChange={set('total_rsf')} disabled={disabled} inputMode="numeric" />
            <Input label="Average occupancy % (actual)" value={f.occupancy_pct} onChange={set('occupancy_pct')} disabled={disabled} inputMode="decimal" hint="Used for gross-up at year end" />
            <Input label="Notes" value={f.notes} onChange={set('notes')} disabled={disabled} />
            {!disabled && <div className="mb-3 flex gap-2">
                <Button variant="ghost" onClick={() => onSave(f)}>Save</Button>
                {pool.status === 'actual' && <Button variant="subtle" onClick={() => onSave({ ...f, status: 'closed' })}>Close the year</Button>}
            </div>}
        </div>
    );
}
